import React, { useMemo } from 'react';
import classnames from 'classnames';
import { Radio } from 'tea-component';
import { useHotEventStatsComments } from '../../../hooks/useHotEventStatsComments';
import { TDateType, useHotEventTimeline } from '../../../hooks/useHotEventTimeline';
import useLanguage from '@op/hooks/useLanguage';
import { transLanguage } from '@op/utils';
import ChannelLabel from '@op/components/ChannelLabel';
import { IDiscussionItem } from '../types';
import CardCommon from '@op/components/CardCommon';
import EventTrackingChart from './EventTrackingChart';
import './index.scss';

interface OverviewLanguagesProps {
  item: IDiscussionItem;
  filterParams: Record<string, any> | undefined;
}

// ── StatBarPanel 内部组件 ──

type TColumnConfig = {
  title: string;
  list: any[];
  getKey: (item: any) => string;
  renderLabel: (item: any) => React.ReactNode;
  barColor: string;
};

function StatBarPanel({ columns, mode = 'horizontal' }: { columns: TColumnConfig[]; mode?: 'horizontal' | 'vertical' }) {
  return (
    <div className={classnames('dbo-event-overview-languages', {
      'dbo-event-overview-languages--vertical': mode === 'vertical',
    })}>
      {columns.map((col) => (
        <div key={col.title} className="dbo-event-overview-languages-col">
          <div className="dbo-event-overview-languages-col-title">{col.title}</div>
          {col.list.map((t: any) => (
            <div key={col.getKey(t)} className="dbo-event-overview-stat-row">
              <span className="dbo-event-overview-stat-label">
                {col.renderLabel(t)}
              </span>
              <div className="dbo-event-overview-stat-bar-track">
                <div
                  className="dbo-event-overview-stat-bar-fill"
                  style={{
                    width: `${t.percentage}%`,
                    backgroundColor: col.barColor,
                  }}
                />
              </div>
              <span className="dbo-event-overview-stat-value">
                {t.mentions} ({t.percentage}%)
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Event Tracking 内部组件 ──

interface EventTrackingProps {
  eventValue: string;
  lineData: { x: any[]; y: any[]; empty: boolean };
  clusterName: string;
  days: number;
  onDateTypeChange: (value: string) => void;
  loading?: boolean;
}

function EventTracking({ eventValue, lineData, clusterName, days, onDateTypeChange, loading }: EventTrackingProps) {
  const { formatMessage } = useLanguage();

  return (
    <div className="dbo-event-overview-tracking">
      <div className="dbo-event-overview-tracking-title">
        {formatMessage({ id: 'Event Tracking' })}
      </div>
      <Radio.Group
        value={eventValue}
        onChange={(value) => onDateTypeChange(value)}
      >
        <Radio name="week">1 {formatMessage({ id: 'Week' })}</Radio>
        <Radio name="month">1 {formatMessage({ id: 'Month' })}</Radio>
        <Radio name="all">{formatMessage({ id: 'All' })}</Radio>
      </Radio.Group>
      <div className="dbo-event-overview-tracking-chart">
        <EventTrackingChart
          clusterName={clusterName}
          data={lineData}
          days={days}
          loading={loading}
        />
      </div>
    </div>
  );
}

// ── 主组件 ──

/**
 * Overview Language/Channel 分布条 + Event Tracking 组件
 * 内部封装 useHotEventStatsComments / useHotEventTimeline 数据请求
 */
export default function OverviewLanguages({ item, filterParams }: OverviewLanguagesProps) {
  const { formatMessage, isEn } = useLanguage();
  const { statsData, loading: statsLoading } = useHotEventStatsComments(item, filterParams);
  const { eventValue, lineData, getTimeline, loading, setEventValue } = useHotEventTimeline(item, filterParams);

  // ── 列配置（统一驱动 Language / Channel 渲染） ──
  const columns = useMemo(() => {
    const sortAndSlice = (list: any[]) =>
      [...list]
        .sort((a, b) => (b.value || b.mentions || 0) - (a.value || a.mentions || 0))
        .slice(0, 3);

    return [
      {
        title: formatMessage({ id: 'Language' }),
        list: sortAndSlice(statsData?.language_list || []),
        getKey: (t: any) => t.language,
        renderLabel: (t: any) => transLanguage(t.language),
        barColor: '#5086F3',
      },
      {
        title: formatMessage({ id: 'Channel' }),
        list: sortAndSlice(statsData?.channel_list || []),
        getKey: (t: any) => t.channel,
        renderLabel: (t: any) => <ChannelLabel channel_name={t.channel} />,
        barColor: '#69D19C',
      },
    ];
  }, [statsData, formatMessage]);

  const isEmpty = columns.every((col) => col.list.length === 0);
  return (
    <CardCommon className="dbo-event-overview-summary-content-language" contentCard loading={statsLoading} hasEmptyImg={false} loadingHeight={100} empty={isEmpty} emptyHeight={100}>
      <StatBarPanel columns={columns} />
      {/* <EventTracking
        eventValue={eventValue}
        lineData={lineData}
        clusterName={isEn ? item.event_title : item.event_title_cn}
        days={filterParams?.days || 1}
        loading={loading}
        onDateTypeChange={(value) => {
          setEventValue(value as TDateType)
          getTimeline(value as TDateType);
        }}
      /> */}
    </CardCommon>
  );
}
